/**
 * "Who looked at whom" — the audit trail every per-user view writes (D7),
 * shown collapsed at the bottom of the investigate page.
 *
 * Collapsed and lazy: the list is itself a list of named people (viewer and
 * subject), so it is fetched only when someone opens it, never persisted, and
 * forgotten two minutes after it closes. It is on the page at all because an
 * audit log nobody can see is a log nobody trusts — the investigator should be
 * able to show Helle, the works council or a colleague exactly what their own
 * session wrote.
 */

import { ChevronDown, Icon } from "@norican/icons";
import { Alert, Collapsible, DataGrid, EmptyState, Skeleton, Typography } from "@norican/react";
import { useState } from "react";

import { type FoConsoleAuditEntry, useFoConsoleAudit } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatBinDateTime } from "./status";

/** The backend caps the list; this is the "last N" the trigger promises. */
const AUDIT_LIMIT = 200;

export function AuditSection() {
  const [open, setOpen] = useState(false);
  const audit = useFoConsoleAudit(AUDIT_LIMIT, open);
  const list = audit.data;
  const entries = list?.entries ?? [];

  return (
    <PanelCard
      title="Who looked at whom"
      caption={`Every per-user view, newest first, last ${AUDIT_LIMIT} · fetched only when opened`}
    >
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger className="text-fg inline-flex items-center gap-2 rounded text-sm font-medium hover:underline focus-visible:outline-2">
          <Icon
            icon={ChevronDown}
            size="sm"
            aria-hidden="true"
            className={
              open
                ? "rotate-180 motion-safe:transition-transform"
                : "motion-safe:transition-transform"
            }
          />
          {open ? "Hide the audit list" : "Show the audit list"}
        </Collapsible.Trigger>
        <Collapsible.Panel>
          <div className="mt-3 flex flex-col gap-3">
            {audit.error && (
              <Alert tone="danger" title="Couldn't read the audit list">
                The NOVA API did not answer.
              </Alert>
            )}
            {/* `available: false` is the audit store itself being unreadable —
                a different fact from "nobody has looked yet", and the louder one:
                per-user views without a working audit trail are what D7 forbids. */}
            {list && !list.available && (
              <Alert tone="danger" title="The audit trail is not available">
                {list.detail}
              </Alert>
            )}
            {audit.isPending && open && !list ? (
              <Skeleton height="12rem" />
            ) : entries.length === 0 ? (
              list?.available && (
                <EmptyState
                  size="sm"
                  title="No per-user view has been logged yet"
                  description="The list fills the first time anyone opens a person's timeline or a record."
                />
              )
            ) : (
              <>
                <div className="h-80">
                  <DataGrid<FoConsoleAuditEntry>
                    data={entries}
                    getRowId={(row) =>
                      `${row.ts}-${row.viewer}-${row.subject_mail}-${row.endpoint}`
                    }
                    density="compact"
                    aria-label="Audit trail of per-user views"
                    columns={[
                      {
                        id: "ts",
                        header: "When",
                        accessor: (row) => row.ts,
                        sortable: true,
                        cell: (row) => (
                          <span className="tabular-nums">{formatBinDateTime(row.ts)}</span>
                        ),
                      },
                      {
                        id: "viewer",
                        header: "Viewer",
                        accessor: (row) => row.viewer,
                        sortable: true,
                      },
                      {
                        id: "subject",
                        header: "Looked at",
                        accessor: (row) => row.subject_mail,
                        sortable: true,
                      },
                      { id: "endpoint", header: "View", accessor: (row) => row.endpoint },
                      { id: "lookback", header: "Lookback", accessor: (row) => row.lookback },
                      {
                        id: "activity",
                        header: "Record",
                        accessor: (row) => row.activity_id,
                        cell: (row) =>
                          row.activity_id ? (
                            <code className="text-xs break-all">{row.activity_id}</code>
                          ) : (
                            <span className="text-fg-subtle">—</span>
                          ),
                      },
                      { id: "reason", header: "Reason", accessor: (row) => row.reason },
                    ]}
                  />
                </div>
                <Typography variant="caption" tone="muted">
                  Written by the backend on every `/fo/console/user` and `/fo/console/record` call.
                  The reason is whatever the viewer typed; the backend does not verify it against a
                  ticket system.
                </Typography>
              </>
            )}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </PanelCard>
  );
}
